// src/pages/batch/Batch_List_ForTeaching.jsx
import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardTitle, Row, Col, Button } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../constants/ApiService';

import useVendorBranch from '../../utils/useVendorBranch';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import { useSelector } from 'react-redux';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { toast } from 'react-toastify';

const BatchCardList = ({
  actionButtonText = 'Log Today’s Teaching',
  onActionClick,
  apiUrl,
  responseDataKey,
}) => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState({});
  const { user } = useSelector((state) => state.authState);

  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });

  const [batches, setBatches] = useState([]);
  const [search, setSearch] = useState('');

  // ✅ NEW: Class dropdown states (same as Admission pattern)
  const [classMasterData, setClassMasterData] = useState([]);
  const [curr_class, setcurr_class] = useState(''); // store classId (preferred)

  // ✅ NEW: load class master list (API)
  const loadClassMasterData = async () => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      const response = await apiService.get(
        `api/ClassMaster/GetClassList?vendorId=${vendorId}&branchId=${branchId}&`
      );

      setClassMasterData(response.classList);
    } catch (error) {
      console.log(error);
      alert('SomeThing went wrong');
    }
  };

  const loadBatches = async (searchText = '') => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      // ✅ CHANGED: classId now comes from dropdown (curr_class), else 0
      const classId = curr_class ? curr_class : 0;

      const response = await apiService.get(
        `${apiUrl}?classId=${classId}&vendorId=${vendorId}&branchId=${branchId}&search=${encodeURIComponent(
          searchText || ''
        )}`
      );

      if (response?.status === true) {
        const resultData = response?.[responseDataKey] || [];
        setBatches(resultData);
      } else {
        setBatches([]);
        toast.error(response?.message || 'No batch found');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong!!');
      setBatches([]);
    }
  };

  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    loadBatches(val);
  };

  const openTeachingForm = (batch) => {
    debugger;
    navigate('/save-teaching', {
      state: {
        class_id: batch.class_id,
        batch_id: batch.id,
        class_name: batch.class_name,
        batch_name: batch.batch_name ?? batch.batchName,
      },
    });
  };

  // ✅ existing vendor/branch load behaviour (same), now also load class dropdown
  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });

    if (canLoad) {
      loadClassMasterData(); // ✅ NEW
      loadBatches(); // existing
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionData.vendor_id, selectionData.branch_id]);

  // ✅ NEW: when class changes => reload batches (keep other functions same)
  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });

    if (canLoad) {
      loadBatches(search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curr_class]);

  return (
    <Col lg='12'>
      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />
      <Card className='card-box height-100-p pd-20 mb-20'>
        <CardTitle
          tag='h6'
          className='border-bottom p-3 mb-0 d-flex justify-content-between align-items-center'
        >
          <span>
            <i className='bi bi-grid-3x3-gap-fill me-2' />
            Batch List
          </span>

          <div className='col-12 col-md-5'>
            <div className='input-group'>
              <span className='input-group-text'>Class</span>

              <select
                name='curr_class'
                value={curr_class}
                onChange={(e) => setcurr_class(e.target.value)}
                className='custom-select'
                aria-label='Status curr_class'
              >
                <option value=''>-Select-</option>
                {classMasterData.map((opt, idx) => {
                  return (
                    <option key={idx} value={opt.id}>
                      {opt.class_Name}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </CardTitle>

        <CardBody>
          <Row>
            {batches && batches.length > 0 ? (
              batches.map((b) => {
                const batchCode = b.batch_code ?? 'N/A';
                const batchTime =
                  b.batch_start_time + ' - ' + b.batch_end_time ?? 'Time N/A';
                const courseName = b.class_name ?? 'Course N/A';

                return (
                  <Col key={b.id} sm='6' md='4' lg='3' className='mb-4'>
                    <Card className='h-100 border shadow batch-card'>
                      <CardBody className='d-flex flex-column'>
                        {/* Header */}
                        <div className='mb-2'>
                          <h6 className='mb-1 fw-semibold text-primary'>
                            {batchCode}
                          </h6>

                          <div className='d-flex align-items-center text-muted small'>
                            <i className='bi bi-clock mr-1'></i>
                            {batchTime}
                          </div>
                        </div>

                        {/* Divider */}
                        <hr className='my-2' />

                        {/* Details */}
                        <div className='mb-2 small'>
                          <div className='d-flex justify-content-between'>
                            <span className='text-muted'>Course</span>
                            <span className='fw-medium'>{courseName}</span>
                          </div>

                          <div className='d-flex justify-content-between mt-1'>
                            <span className='text-muted'>Subject</span>
                            <span className='fw-medium'>{b.subject_name}</span>
                          </div>
                        </div>

                        {/* Action */}
                        <Button
                          color='primary'
                          size='sm'
                          className='mt-auto w-100'
                          onClick={() => onActionClick(b)}
                        >
                          <i className='bi bi-journal-check mr-1'></i>
                          {actionButtonText}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })
            ) : (
              <Col>
                <p className='text-center text-muted m-0'>No batches found</p>
              </Col>
            )}
          </Row>
        </CardBody>
      </Card>
    </Col>
  );
};

export default BatchCardList;
